"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import { useClientValue } from "@/lib/use-client-value";
import { usePathname } from "next/navigation"

type FrozenContextType = {
  isFrozen: boolean;
  setIsFrozen: (value: boolean) => void;
};

const FrozenContext = createContext<FrozenContextType | undefined>(undefined);

function getCookie(name: string) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function readFrozenCookie() {
  return getCookie("rc_is_frozen") === "true";
}

export function FrozenProvider({ children }: { children: ReactNode }) {
  // Subscribes the provider to route changes so the cookie is re-read on every
  // navigation, which is what the effect this replaced did. The value itself is
  // not needed.
  usePathname();

  // The cookie is the source of truth. setIsFrozen stays on the context because
  // it is part of its published shape; an explicit call overrides the cookie
  // until the next reload.
  const cookieFrozen = useClientValue(readFrozenCookie, false);
  const [override, setOverride] = useState<boolean | null>(null);
  const isFrozen = override ?? cookieFrozen;
  const setIsFrozen = setOverride;

  return (
    <FrozenContext.Provider value={{ isFrozen, setIsFrozen }}>{children}</FrozenContext.Provider>
  );
}

export function useFrozen() {
  const context = useContext(FrozenContext);
  if (!context) {
    throw new Error("useFrozen must be used inside FrozenProvider");
  }
  return context;
}
